import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axiosInstance';
import axios from 'axios';
import { toast } from 'sonner';

const BASE_URL = import.meta.env.VITE_APP_API_URL;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const saveTokens = (accessToken, refreshToken, user) => {
  localStorage.setItem('authx_access_token', accessToken);
  localStorage.setItem('authx_refresh_token', refreshToken);
  localStorage.setItem('authx_user', JSON.stringify(user));
};

const clearTokens = () => {
  localStorage.removeItem('authx_access_token');
  localStorage.removeItem('authx_refresh_token');
  localStorage.removeItem('authx_user');
  localStorage.removeItem('authx_menu'); // Clear menu too
};

const loadUserFromStorage = () => {
  try {
    const user = localStorage.getItem('authx_user');
    const token = localStorage.getItem('authx_access_token');
    if (user && token) return { user: JSON.parse(user), token };
    return null;
  } catch {
    return null;
  }
};

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const loginUser = createAsyncThunk('auth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    const { data } = await axios.post(`${BASE_URL}Auth/login`, { email, password });
    if (!data.success) throw new Error(data.message || 'Login failed');
    const { accessToken, refreshToken, user, menu } = data.data;
    saveTokens(accessToken, refreshToken, user);
    // Save menu to localStorage for persistence across refresh
    if (menu) {
      localStorage.setItem('authx_menu', JSON.stringify(menu));
    }
    return { accessToken, refreshToken, user, menu: menu || [] };
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Login failed.';
    toast.error(msg);
    return rejectWithValue(msg);
  }
});

export const logoutUser = createAsyncThunk('auth/logout', async (_, { getState }) => {
  try {
    const { auth } = getState();
    if (auth.user?.userId) {
      await api.post('Auth/revoke');
    }
  } catch {
  } finally {
    clearTokens();
  }
});

export const refreshAccessToken = createAsyncThunk('auth/refresh', async (_, { rejectWithValue }) => {
  const accessToken = localStorage.getItem('authx_access_token');
  const refreshToken = localStorage.getItem('authx_refresh_token');
  if (!refreshToken) return rejectWithValue('No refresh token');
  try {
    const { data } = await axios.post(`${BASE_URL}Auth/refresh`, { accessToken, refreshToken });
    if (!data.success) throw new Error(data.message);
    const { accessToken: newAccess, refreshToken: newRefresh, user, menu } = data.data;
    saveTokens(newAccess, newRefresh, user);
    if (menu) localStorage.setItem('authx_menu', JSON.stringify(menu));
    return { accessToken: newAccess, refreshToken: newRefresh, user, menu: menu || [] };
  } catch (err) {
    clearTokens();
    return rejectWithValue(err.message);
  }
});

// ─── Slice ────────────────────────────────────────────────────────────────────
const stored = loadUserFromStorage();
const storedMenu = (() => {
  try {
    const m = localStorage.getItem('authx_menu');
    return m ? JSON.parse(m) : [];
  } catch {
    return [];
  }
})();

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: stored?.user || null,
    accessToken: stored?.token || null,
    isAuthenticated: !!stored?.token,
    menu: storedMenu, // <-- NEW: menu stored in auth state
    isOwner: stored?.user?.isOwner || false,
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setTokens: (state, action) => {
      state.accessToken = action.payload.accessToken;
      if (action.payload.user) state.user = action.payload.user;
    },
    updateCompanyLogo: (state, action) => {
      if (state.user) {
        state.user.companyLogo = action.payload;
        const stored = JSON.parse(localStorage.getItem('authx_user') || '{}');
        stored.companyLogo = action.payload;
        localStorage.setItem('authx_user', JSON.stringify(stored));
      }
    },
    setMenu: (state, action) => {
      state.menu = action.payload;
      localStorage.setItem('authx_menu', JSON.stringify(action.payload));
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(loginUser.fulfilled, (s, a) => {
        s.loading = false;
        s.user = a.payload.user;
        s.accessToken = a.payload.accessToken;
        s.isAuthenticated = true;
        s.menu = a.payload.menu || [];
        s.isOwner = a.payload.user?.isOwner || false;
        s.error = null;
      })
      .addCase(loginUser.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
        s.isAuthenticated = false;
      })
      .addCase(logoutUser.fulfilled, (s) => {
        s.user = null;
        s.accessToken = null;
        s.isAuthenticated = false;
        s.error = null;
        s.menu = [];
        s.isOwner = false;
      })
      .addCase(refreshAccessToken.fulfilled, (s, a) => {
        s.accessToken = a.payload.accessToken;
        s.user = a.payload.user;
        s.isAuthenticated = true;
        if (a.payload.menu?.length) s.menu = a.payload.menu;
        s.isOwner = a.payload.user?.isOwner || false;
      })
      .addCase(refreshAccessToken.rejected, (s) => {
        s.user = null;
        s.accessToken = null;
        s.isAuthenticated = false;
        s.menu = [];
      });
  }
});

export const { clearError, setTokens, updateCompanyLogo, setMenu } = authSlice.actions;
export default authSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────
export const selectCurrentUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectUserRoles = (state) => state.auth.user?.roles || [];
export const selectCompanyId = (state) => state.auth.user?.companyId;
export const selectCompanyName = (state) => state.auth.user?.companyName || '';
export const selectCompanyLogo = (state) => state.auth.user?.companyLogo || null;
export const selectIsAdmin = (state) => (state.auth.user?.roles || []).includes('Admin');
export const selectIsManager = (state) => (state.auth.user?.roles || []).includes('Manager');
export const selectIsSupport = (state) => (state.auth.user?.roles || []).includes('Support');
export const selectUserMenu = (state) => state.auth.menu || []; // NEW
export const selectIsOwner = (state) => state.auth.isOwner || false;
